// @ts-check
import {
  collection,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { db } from "./firebase_db";
import { getEmail } from "../firebase_auth";
import { getUserProjects, saveProjectUser } from "./firebase_project_users";

/**
 * @import { Project } from "./project";
 * @import { Unsubscriber } from "./unsubscriber";
 * @import {DocumentSnapshot} from 'firebase/firestore'
 */

export const projects = collection(db, "projects");

async function getProjectsQuery() {
  const email = getEmail();
  const projectsId = (await getUserProjects(email)).map((pu) => pu.project);

  const filter = where("uid", "in", projectsId);
  const q = query(projects, filter);
  return q;
}

/**
 *
 * @param {DocumentSnapshot} doc
 * @returns {Project}
 */
function getProjectFromDoc(doc) {
  /** @type {object} */
  const project = {
    ...doc.data(),
    id: doc.id,
  };
  return project;
}

/**
 * @returns {Promise<Array<Project>>}
 */
export async function getProjects() {
  try {
    const q = await getProjectsQuery();
    const snapshot = await getDocs(q);

    const projectsList = [];
    snapshot.forEach((doc) => projectsList.push(getProjectFromDoc(doc)));

    return projectsList;
  } catch (e) {
    // console.error(e);
    return [];
  }
}

/**
 * @param {(projects: Project[]) => void} onNewProjects
 * @param {(projects: Project[]) => void} onModifiedProjects
 * @param {(projects: Project[]) => void} onRemovedProjects
 * @returns {Promise<Unsubscriber>}
 */
export async function subscribeProjects(
  onNewProjects,
  onModifiedProjects,
  onRemovedProjects,
) {
  try {
    const q = await getProjectsQuery();
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newProjects = [];
      const modifiedProjects = [];
      const removedProjects = [];

      snapshot.docChanges().forEach((change) => {
        if (change.type === "added")
          newProjects.push(getProjectFromDoc(change.doc));
        else if (change.type === "modified")
          modifiedProjects.push(getProjectFromDoc(change.doc));
        else if (change.type === "removed")
          removedProjects.push(getProjectFromDoc(change.doc));
      });

      if (newProjects.length > 0) onNewProjects(newProjects);
      if (modifiedProjects.length > 0) onModifiedProjects(modifiedProjects);
      if (removedProjects.length > 0) onRemovedProjects(removedProjects);
    });
    return {
      invoke: () => unsubscribe(),
    };
  } catch (e) {
    console.error(e);
    return {
      invoke: () => console.log("void unsubscriber"),
    };
  }
}

/**
 * @param {Project} project
 * @returns {Promise<Project>}
 */
export async function saveProject(project) {
  try {
    const email = getEmail();
    project.modifiedBy = email;
    const { id, ...data } = project;

    if (id) {
      await setDoc(doc(projects, id), data);
      return project;
    }

    // const ref = await addDoc(projects, { ...data, owner: email });
    const ref = doc(projects, project.uid);
    await setDoc(ref, { ...data, owner: email });
    project.id = ref.id;
    project.owner = email;
    await saveProjectUser({
      project: project.id,
      user: email,
      role: "OWNER",
    });
    return project;
  } catch (e) {
    console.error(e);
    return project;
  }
}

/**
 * @param {string} id
 */
export async function removeProjectById(id) {
  await deleteDoc(doc(projects, id));
}

/**
 *
 * @param {Project} project
 * @returns {Promise<void>}
 */
export async function removeProject(project) {
  if (!project.id) return;
  await removeProjectById(project.id);
}
